import React, { useState } from "react";
import { Mail, CheckCircle2 } from "lucide-react";
import { api } from "../lib/api";

/**
 * NewsletterSubscribe — inline form used in Footer/Home.
 * Props:
 *   variant: "footer" (default) | "compact" | "hero"
 *   source: label to attribute in DB
 */
export default function NewsletterSubscribe({ variant = "footer", source = "footer" }) {
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [state, setState] = useState({ loading: false, ok: false, error: "" });

  const submit = async (e) => {
    e.preventDefault();
    if (state.loading) return;
    setState({ loading: true, ok: false, error: "" });
    try {
      const { data } = await api.post("/newsletter/subscribe", {
        email: email.trim().toLowerCase(),
        marketing_consent: consent,
        source,
      });
      setState({
        loading: false,
        ok: true,
        error: "",
        already: data.already_subscribed,
      });
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Non è stato possibile completare l'iscrizione. Riprova.";
      setState({ loading: false, ok: false, error: msg });
    }
  };

  if (state.ok) {
    return (
      <div
        data-testid="newsletter-success"
        className="rounded-2xl bg-[#0d162d]/70 border border-[#1f2c4a] p-5 text-sm text-white flex items-start gap-3"
      >
        <CheckCircle2 className="text-[#FBD51C] mt-0.5" size={20} />
        <div>
          <div className="font-bold mb-1">
            {state.already ? "Iscrizione confermata" : "Iscrizione completata!"}
          </div>
          <p className="text-zinc-400 leading-relaxed">
            Ti abbiamo mandato un&apos;email di benvenuto. Ogni venerdì mattina
            riceverai le migliori offerte della settimana.
          </p>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid="newsletter-form"
      className={
        variant === "hero"
          ? "rounded-2xl bg-zinc-950/70 border border-zinc-800 p-5 backdrop-blur"
          : "rounded-2xl bg-zinc-900/60 border border-zinc-800 p-5"
      }
    >
      <div className="flex items-center gap-2 mb-3">
        <Mail size={16} className="text-[#FBD51C]" />
        <div className="font-mono-bdd text-[10px] uppercase tracking-[0.3em] text-[#FBD51C]">
          Newsletter settimanale
        </div>
      </div>
      <div className="font-display font-black text-lg text-white leading-snug mb-1">
        Le migliori offerte, ogni venerdì.
      </div>
      <p className="text-xs text-zinc-500 mb-4 leading-relaxed">
        Ricevi la selezione curata dei migliori sconti su LEGO e set di
        costruzioni. Niente spam. Disiscrizione con un click.
      </p>

      <div className="flex flex-col sm:flex-row gap-2">
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="tua@email.com"
          data-testid="newsletter-email-input"
          className="flex-1 bg-black/40 border border-zinc-800 focus:border-[#FBD51C] outline-none rounded-lg px-3 py-2.5 text-sm text-white placeholder-zinc-600"
        />
        <button
          type="submit"
          disabled={state.loading || !consent}
          data-testid="newsletter-submit"
          className="bg-[#FBD51C] hover:bg-[#F5C518] disabled:bg-zinc-800 disabled:text-zinc-500 text-black font-black text-sm uppercase tracking-wider px-5 py-2.5 rounded-lg transition-colors whitespace-nowrap"
        >
          {state.loading ? "Iscrivo…" : "Iscriviti"}
        </button>
      </div>

      <label className="mt-3 flex items-start gap-2 text-[11px] text-zinc-500 leading-relaxed cursor-pointer">
        <input
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          data-testid="newsletter-consent"
          className="mt-0.5 accent-[#FBD51C]"
        />
        <span>
          Acconsento a ricevere via email la newsletter con le offerte e
          contenuti editoriali. Leggi la nostra{" "}
          <a href="/privacy" className="underline hover:text-[#FBD51C]">
            Privacy Policy
          </a>
          .
        </span>
      </label>

      {state.error && (
        <div
          data-testid="newsletter-error"
          className="mt-3 text-xs text-rose-400"
        >
          {state.error}
        </div>
      )}
    </form>
  );
}
